import React, { useRef } from 'react'
import { Services } from '@/services/services';
import { CurrentSession, SessionService } from '@/services/repository/session';
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout';
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable';
import { formatDate } from '@/common/time';
import { isDesktopScreen, useResponsiveChange } from '@/common/device';
import { useNavigate } from 'react-router-dom';
import { useModal } from '@/components/modal/Modal';
import { BackButton } from '@/components/layout/NavBar';
import { Translates } from '@/services/repository/i18n';
import { Menu, MenuPopupBody } from '@/popups/menupopup/menu';
import { MachinePopupQRCodeBody } from '@/popups/qrcode/qrcode';
import { MachinePopupDocBody } from '@/popups/docs/docs';
import MachineMapComponent from '@/components/Map/MachineMap';
import { CardHistoryBody } from '@/pages/communal/machine/components/CardHistory';
import { role } from '@/components/forms/users/User';
import { Roles } from '@/services/repository/users';
import { fetchCSV, fetchExcel } from '@/common/fetch';
import { useAppContext } from '@/context/app';

export interface SimpleMachine {
  id: string
  imei: string

  manufacturer: string
  model: string
  serialNumber: string
  internalNumber: string
  machineTypeId: string
  name: string
}

// const fetchCSV = async ({ services, session }: { services: Services, session: CurrentSession }) => {
//   try {
//     const response = await services.session.fetchWithToken('/api/communal/my/machines/csv', {
//       method: 'GET',
//       headers: {
//         'Accept': 'text/csv',
//       },
//     })
//     if (!response.ok) {
//       throw new Error(`HTTP error! Status: ${response.status}`)
//     }
//     const csvData = await response.text()
//     const blob = new Blob([csvData], { type: 'text/csv' })
//     const downloadUrl = window.URL.createObjectURL(blob)
//     const a = document.createElement('a')
//     a.href = downloadUrl
//     a.download = 'machines.csv'
//     document.body.appendChild(a)
//     a.click()
//     a.remove()
//     window.URL.revokeObjectURL(downloadUrl)
//   } catch (error) {
//     console.error('Error fetching CSV:', error)
//   }
// }

export const MachineMapPopup = ({ services, session, onClose, imei, title }: {
  services: Services,
  session: CurrentSession,
  onClose: () => void
  imei: string,
  title?: string
}) => {
  return <>
    {title ? <h3>{title}</h3> : null}
    <MachineMapComponent services={services} session={session} imei={imei} />
  </>
}

const MenuPopup = ({ services, session, translates, onClose, row }: {
  translates: Translates,
  services: Services,
  session: CurrentSession,
  onClose: () => void
  row: SimpleMachine
}) => {
  const navigate = useNavigate()
  const { openModal } = useModal()
  const title = `${row.manufacturer || ''} ${row.model || ''} ${row.internalNumber ? row.internalNumber : row.serialNumber || ''}`
  const menu: Menu = {
    title,
    rows: [
      // {
      //   icon: 'svgfont-password',
      //   title: 'elements.table.users.menu.edit.password',
      //   action: () => openModal((props) => SuperAdminNewPasswordPopup({ ...props, id: row.id, services, session })),
      // },
      // {
      //   icon: 'svgfont-users',
      //   title: 'elements.table.users.menu.edit.groups',
      //   action: () => openModal((props) => RelatedUserGroupsPage({ ...props, userId: row.id, services, session })),
      // },
      // {
      //   icon: 'svgfont-edit',
      //   title: 'elements.table.users.menu.edit.edit_row',
      //   action: () => openModal((props) => SuperAdminEditUsersPopup({ ...props, id: row.id, services, session })),
      // },
      // {
      //   icon: 'svgfont-remove',
      //   title: 'elements.table.users.menu.edit.remove',
      //   action: () => openModal((props) => SuperAdminRemoveUsersPopup({ ...props, user: row, services, session })),
      // },
      {
        icon: 'svgfont-link',
        title: 'elements.table.machines.menu.edit.open_machine_page',
        action: () => navigate(`/communal/machine/${row.id}`)
      },
      {
        icon: 'svgfont-user-card',
        title: 'elements.table.machines.menu.edit.show_card_history',
        action: () => openModal((props) => CardHistoryBody({ ...props, machineId: row.id, services, session, title }))
      },
      {
        icon: 'svgfont-qrcode',
        title: 'elements.table.machines.menu.edit.show_qr_code',
        action: () => openModal((props) => MachinePopupQRCodeBody({ ...props, machineId: row.id, services, session, translates: session.translates, title }))
      },
      {
        icon: row.machineTypeId ? 'svgfont-documentation' : '-',
        title: 'elements.table.machines.menu.edit.show_docs',
        action: () => row.machineTypeId && openModal((props) => MachinePopupDocBody({ ...props, machineTypeId: row.machineTypeId, services, session, translates: session.translates, title }))
      },
      ...(row.imei ? [{
        icon: 'svgfont-map',
        title: 'elements.table.machines.menu.edit.machine_map',
        action: () => openModal((props) => MachineMapPopup({ ...props, imei: row.imei, services, session, title }))
      },
      {
        icon: 'svgfont-calendar',
        title: 'elements.table.machines.menu.edit.pathmap',
        action: () => navigate(`/communal/machine/${row.id}/pathmap`)
      }] : []),
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

const ExportPopup = ({ onClose,  }: {
  onClose: () => void
}) => {
  const { services, session } = useAppContext()

  const menu: Menu = {
    title: "popup.export.machines_export_title",
    rows: [
      {
        title: 'popup.export.excel_export',
        action: () => fetchExcel({
          services,
          url: '/api/communal/my/machines/excel',
          fileName: 'machines.xlsx'
        })
      },
      {
        title: 'popup.export.csv_export',
        action: () => fetchCSV({
          services,
          url: '/api/communal/my/machines/csv',
          fileName: 'machines.csv'
        })
      }
    ]
  }

  return <MenuPopupBody session={session} menu={menu} onClose={onClose} />
}

export const CompanyManagerMyMachinesPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const navigate = useNavigate()
  const { openModal } = useModal()

  useResponsiveChange()

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      return services.session.fetchWithToken(`/api/communal/my/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    // toolbox: () => <></>,
    toolbox: () => <>
      {[Roles.CompanyAdmin, Roles.CompanyManager, Roles.SuperAdmin].includes(session.user.role as Roles) ? <>
        <div className='tool' onClick={() => openModal((props) => ExportPopup({ ...props }))}>
          <img src="/public/img/icons/export-file.svg" />
        </div>
      </> : null}
    </>,
    header: () => <>
      <div className='hcol'>{session.translates.for('elements.table.machines.manufacturer')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.model')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.number')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.machines.name')}</div> */}
      <div className='hcol'></div>
    </>,
    row: (row) => {
      return <>
        <div className='col'>{row.manufacturer || '-'}</div>
        <div className='col'>{row.model || '-'}</div>
        <div className='col'>{row.internalNumber ? row.internalNumber : row.serialNumber || row.id}</div>
        {/* <div className='col'>{row.name || '-'}</div> */}
        <div className='col icocol' onClick={() => openModal((props) => MenuPopup({ ...props, row: row, services, session, translates: session.translates }))}>
          <i className='svgfont-menu' />
        </div>
      </>
    },
    translates: session.translates
  }

  return <LoggedInPageLayout pageName='my-machines' services={services} session={session}
    navbarLeft={<BackButton services={services} translates={session.translates} />}>
    <SearchTable ref={searchTableRef} {...tableParams} />
  </LoggedInPageLayout>
}
